import * as React from 'react';
import { Box, BoxProps, MenuItem, styled } from '@mui/material';
import { Autocomplete, AutocompleteProps } from 'formik-mui';

import { fontFamily, weights } from '../context';

import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

export function CerebroAutoComplete<
	TOption,
	Multiple extends boolean | undefined,
	DisableClearable extends boolean | undefined,
	FreeSolo extends boolean | undefined
>({
	error,
	getOptionLabel = option => option as any as string,
	...props
}: CerebroAutoCompleteProps<TOption, Multiple, DisableClearable, FreeSolo>): ReturnType<React.FC> {
	return (
		<Autocomplete
			{...props}
			noOptionsText="No Matches Found"
			ChipProps={{ size: 'small', color: 'primary' }}
			getOptionLabel={getOptionLabel}
			renderOption={(liProps, option, { selected, inputValue }) => {
				const label = getOptionLabel(option);
				const matches = match(label, inputValue, { insideWords: true, findAllOccurrences: true });
				const parts = parse(label, matches);

				return (
					<CerebroAutoCompleteMenuItem selected={selected} {...liProps} key={JSON.stringify(option)}>
						{parts.map((part, index) => (
							<span
								key={index}
								style={{
									fontWeight: part.highlight ? weights.bold : weights.regular,
								}}
								dangerouslySetInnerHTML={{ __html: part.text.replaceAll(' ', '&nbsp;') }}
							/>
						))}
					</CerebroAutoCompleteMenuItem>
				);
			}}
			renderInput={({ id, disabled, inputProps, InputProps: { ref, startAdornment, endAdornment } }) => (
				<CerebroAutoCompleteInputWrapper ref={ref} disabled={disabled} error={error ?? false}>
					{startAdornment}
					<CerebroAutoCompleteInput
						id={id}
						disabled={disabled}
						placeholder={props.placeholder}
						{...inputProps}
					/>
					{endAdornment}
				</CerebroAutoCompleteInputWrapper>
			)}
		/>
	);
}

export const CerebroAutoCompleteMenuItem = styled(MenuItem)(({ theme, selected }) => ({
	backgroundColor: selected ? theme.palette.primary.main : 'inherit',

	fontFamily,
	fontSize: 12,

	lineHeight: '14px',
}));

export const CerebroAutoCompleteInput = styled('input')(({ disabled, theme }) => ({
	alignItems: 'center',
	border: 'none',

	backgroundColor: 'transparent',
	color: disabled ? theme.palette.gray.light : theme.palette.gray.dark,
	cursor: disabled ? 'not-allowed' : 'initial',

	fontFamily,
	fontSize: 12,
	fontWeight: disabled ? weights.medium : weights.semibold,

	lineHeight: '14px',
	position: 'relative',
	padding: theme.spacing(0.5),

	outline: 'none',

	'&::placeholder': {
		color: theme.palette.gray.medium,
	},
}));

type WrapperProps = { error: boolean; disabled: boolean } & BoxProps;

// eslint-disable-next-line react/display-name
const Wrapper = React.forwardRef<HTMLDivElement, WrapperProps>(({ error, disabled, ...props }, ref) => (
	<Box ref={ref} {...props} />
));

export const CerebroAutoCompleteInputWrapper = styled(Wrapper, { shouldForwardProp: prop => prop !== 'error' })(
	({ error, disabled, theme }) => ({
		alignItems: 'center',
		borderWidth: 2,
		borderStyle: 'solid',
		borderRadius: theme.shape.borderRadius,
		borderColor: error ? theme.palette.error.main : disabled ? theme.palette.gray.light : theme.palette.gray.medium,

		color: disabled ? theme.palette.gray.light : theme.palette.gray.dark,
		cursor: disabled ? 'not-allowed' : 'initial',

		display: 'flex',

		flexWrap: 'wrap',
		fontFamily,
		fontSize: 12,
		fontWeight: disabled ? weights.medium : weights.semibold,

		lineHeight: '14px',
		marginBottom: theme.spacing(1),
		marginTop: theme.spacing(1.5),
		padding: '14px 8px',
		position: 'relative',

		outline: 'none',
		width: '100%',

		'&:focus:not(:read-only)': {
			borderColor: error ? theme.palette.error.light : theme.palette.primary.main,
		},

		'&::placeholder': {
			color: theme.palette.gray.medium,
		},
	})
);

export interface CerebroAutoCompleteProps<
	TOption,
	Multiple extends boolean | undefined,
	DisableClearable extends boolean | undefined,
	FreeSolo extends boolean | undefined
> extends AutocompleteProps<TOption, Multiple, DisableClearable, FreeSolo> {
	error?: boolean;
}
